import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, MapPin } from 'lucide-react';
import { api } from '../lib/api';
import { useLang } from '../i18n';
import Seo from '../components/Seo';
import { Reveal, Stagger, StaggerItem } from '../components/Reveal';
import { MonogramPattern } from '../components/Brush';
import { ProductTile, TileSkeleton } from '../components/ProductTile';
import { Img } from '../components/Img';
import { TableComposer } from '../components/TableComposer';

export const Shades = () => {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get(`/shades?lang=${lang}`).then((r) => setItems(r.data));
  }, [lang]);

  return (
    <>
      <Seo title={t('shades.title')} description={t('shades.lead')} path="/storie/shades" />

      {/* CONCEPT: cosa sono le Shades + foto ambientata + i cinque colori veri */}
      <header className="py-14 md:py-24">
        <div className="wrap grid items-end gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
          <Reveal>
            <h1 className="font-display text-[clamp(2.5rem,5.5vw,5rem)] leading-[1] tracking-[-0.02em]">{t('shades.title')}</h1>
            <p className="prosa mt-8 border-t-2 border-inchiostro pt-6 text-lg">{t('shades.lead')}</p>
            <a href="#componi" data-testid="shades-hero-cta" className="btn btn-solid mt-9">
              {t('shades.playKicker')}
              <ArrowRight size={15} strokeWidth={2} />
            </a>
          </Reveal>
          <Reveal delay={0.08}>
            {/* Slot foto ambientata (ASSETS A-02): finché il file non c'è resta un
                blocco neutro dimensionato, con la striscia dei cinque colori veri. */}
            <div className="relative aspect-[4/5] overflow-hidden bg-carta-2">
              <Img
                src="/assets/shades/ambient.webp"
                alt={t('shades.photoAlt')}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 flex h-16" aria-hidden>
                {items.map((s) => (
                  <span key={s.slug} className="flex-1" style={{ backgroundColor: s.hex }} />
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </header>

      {/* GIOCA: subito dopo il concept, così l'utente capisce che può comporre */}
      <section id="componi" data-testid="shades-play">
        <div className="wrap pb-2 pt-12 md:pb-4 md:pt-20">
          <h2 className="max-w-[18ch] text-[clamp(2.2rem,5vw,4rem)] leading-[1.02]">
            {t('shades.playTitle')}
          </h2>
        </div>
        <TableComposer />
      </section>

      {/* ESPLORA: le cinque Shades, ognuna con il suo racconto */}
      <section data-testid="shades-index" className="py-14 md:py-24">
        <div className="wrap">
          <h2 className="mb-10 text-3xl md:text-4xl">{t('shades.exploreTitle')}</h2>
          <Stagger className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3" gap={0.08}>
            {items.map((s, i) => (
              <StaggerItem key={s.slug} className={i === 0 ? 'sm:col-span-2' : ''}>
                <Link
                  to={`/storie/shades/${s.slug}`}
                  data-testid={`shade-card-${s.slug}`}
                  className="group flex h-full flex-col"
                >
                  <div
                    className={`relative overflow-hidden ${i === 0 ? 'h-56 md:h-80' : 'h-44 md:h-64'}`}
                    style={{ backgroundColor: s.hex }}
                  >
                    <MonogramPattern className="absolute inset-0" opacity={0.07} />
                    <span className="absolute bottom-4 left-5 font-display text-4xl text-white md:text-5xl">
                      {s.name}
                    </span>
                    <span className="absolute bottom-5 right-5 translate-y-1 opacity-0 transition-[transform,opacity] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                      <ArrowUpRight size={22} strokeWidth={1.6} className="text-white" />
                    </span>
                  </div>
                  <p className="mt-4 text-[14px] font-bold text-marsala">
                    {s.season}
                  </p>
                  {/* Una frase sola per spiegare la shade, quella piccola: il claim
                      in corpo grande diceva la stessa cosa un rigo sopra. */}
                  <p className="mt-2 text-sm leading-relaxed text-inchiostro/70 transition-colors group-hover:text-inchiostro">
                    {s.story}
                  </p>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
};

export const ShadeDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null);
    setMissing(false);
    api
      // limit=96: la shade piu' popolosa ha 41 pezzi, così la pagina li mostra
      // tutti e non serve un rimando a una lista filtrata che non esiste piu'
      .get(`/shades/${slug}?lang=${lang}&limit=96`)
      .then((r) => setData(r.data))
      .catch(() => setMissing(true));
  }, [slug, lang]);

  if (missing) {
    return (
      <div className="wrap py-32">
        <h1 className="display-xl">404</h1>
        <Link to="/storie/shades" className="btn btn-line mt-8">
          {t('shades.back')}
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="wrap py-32">
        <p className="font-display text-3xl text-inchiostro/40">{t('common.loading')}</p>
      </div>
    );
  }

  const byslug = Object.fromEntries((data.others || []).map((o) => [o.slug, o]));
  const pairs = (data.pairs || []).map((p) => byslug[p]).filter(Boolean);

  return (
    <>
      <Seo
        title={`${data.name} · Shade`}
        description={data.claim}
        image={data.products?.[0]?.images?.[0]}
        path={`/storie/shades/${slug}`}
      />

      {/* fascia colore: la Shade è il colore, non la sua descrizione */}
      <header
        data-testid="shade-hero"
        className="relative overflow-hidden text-white"
        style={{ backgroundColor: data.hex }}
      >
        <MonogramPattern className="absolute inset-0" opacity={0.06} />
        <div className="wrap relative py-16 md:py-28">
          <nav aria-label="breadcrumb">
          <Link
            to="/storie/shades"
            data-testid="shade-back"
            className="text-[14px] font-bold text-white/80 transition-colors hover:text-white"
          >
            ← {t('shades.back')}
          </Link>
          </nav>
          <h1 className="display-xl mt-7 !text-white" data-testid="shade-name">
            {data.name}
          </h1>
          <p className="accento mt-5 max-w-[26ch] text-2xl leading-snug text-white/90 md:text-4xl">
            {data.claim}
          </p>
          <p className="mt-8 text-[14px] font-bold text-white/80">
            {data.season}
          </p>
        </div>
      </header>

      {/* racconto stagionale: una frase sola, in corpo leggibile. Il claim in
          testata, la story in display gigante e il manifesto dicevano la stessa
          cosa tre volte in tre corpi diversi. */}
      <section data-testid="shade-story" className="py-12 md:py-20">
        <div className="wrap">
          <Reveal className="max-w-[68ch]">
            <p className="prosa text-lg">{data.manifesto}</p>
            {data.moments?.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2" data-testid="shade-moments">
                <span className="mr-2 self-center text-[14px] font-bold text-inchiostro/70">
                  {t('shades.momentsTitle')}
                </span>
                {data.moments.map((m) => (
                  <span
                    key={m}
                    className="border border-inchiostro/16 px-3 py-1.5 text-[14px]"
                  >
                    {m}
                  </span>
                ))}
              </div>
            )}
          </Reveal>
        </div>
      </section>

      {/* come si usa */}
      {data.notes?.length > 0 && (
        <section data-testid="shade-notes" className="py-12 md:py-16">
          <div className="wrap max-w-3xl">
            <p className="mb-6 text-[14px] font-bold text-inchiostro/70">
              {t('shades.notesTitle')}
            </p>
            <ul>
              {data.notes.map((n, i) => (
                <li key={n.title} className="flex gap-5 py-5">
                  <div>
                    <h3 className="font-display text-xl">{n.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-inchiostro/65">{n.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* i pezzi: slide laterale */}
      <section data-testid="shade-products" className="py-14 md:py-24">
        <div className="wrap flex items-end justify-between gap-4">
          <h2 className="text-3xl md:text-4xl">{t('shades.piecesTitle')}</h2>
          <span className="flex shrink-0 items-center gap-2 text-[14px] font-bold text-inchiostro/70">
            {t('home.scroll')} <ArrowRight size={13} strokeWidth={2.2} />
          </span>
        </div>
        <ol className="mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto px-[var(--gutter)] pb-3 md:gap-7 fila fila-margini">
          {data.products.length === 0
            ? Array.from({ length: 4 }).map((_, i) => (
                <li key={i} className="w-[42%] shrink-0 sm:w-[30%] lg:w-[22%]">
                  <TileSkeleton />
                </li>
              ))
            : data.products.map((p) => (
                <li key={p.sku} className="w-[42%] shrink-0 snap-start sm:w-[30%] lg:w-[22%]">
                  <ProductTile product={p} />
                </li>
              ))}
        </ol>
      </section>

      {/* collezioni che la portano */}
      {data.collections?.length > 0 && (
        <section data-testid="shade-collections" className="py-14 md:py-20">
          <div className="wrap">
            <p className="accento mb-8 text-2xl text-inchiostro/85">{t('shades.collectionsTitle')}</p>
          </div>
          <ol className="flex snap-x snap-mandatory gap-5 overflow-x-auto px-[var(--gutter)] pb-3 fila fila-margini">
            {data.collections.map((c) => (
              <li key={c.slug} className="w-[40%] shrink-0 snap-start sm:w-[28%] lg:w-[16%]">
                <Link
                  to={`/collezioni/${c.slug}`}
                  data-testid={`shade-collection-${c.slug}`}
                  className="group block"
                >
                  <div className="aspect-square overflow-hidden bg-carta-2">
                    {c.image && (
                      <Img
                        src={c.image}
                        alt={c.name}
                        className="img-zoom h-full w-full object-contain p-3 mix-blend-multiply"
                      />
                    )}
                  </div>
                  <h3 className="mt-3 font-display text-lg leading-snug transition-colors group-hover:text-marsala">
                    {c.name}
                  </h3>
                  <p className="text-[14px] text-inchiostro/70">
                    {c.product_count} {t('common.products')}
                  </p>
                </Link>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* abbinamenti e altre shades */}
      <section data-testid="shade-pairs" className="py-14 md:py-20">
        <div className="wrap grid gap-14 lg:grid-cols-2 lg:gap-20">
          {pairs.length > 0 && (
            <Reveal>
              <p className="accento mb-7 text-2xl text-inchiostro/85">{t('shades.pairsTitle')}</p>
              <div className="grid grid-cols-2 gap-5">
                {pairs.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/storie/shades/${p.slug}`}
                    data-testid={`shade-pair-${p.slug}`}
                    className="group block"
                  >
                    <div className="h-28 md:h-36" style={{ backgroundColor: p.hex }} />
                    <p className="mt-3 font-display text-2xl transition-colors group-hover:text-marsala">
                      {p.name}
                    </p>
                    <p className="text-[14px] font-bold text-inchiostro/70">
                      {p.season}
                    </p>
                  </Link>
                ))}
              </div>
            </Reveal>
          )}
          <Reveal delay={0.08} className="flex flex-col justify-between">
            <div>
              <p className="mb-7 text-sm text-inchiostro/65">{t('shades.othersTitle')}</p>
              <ul>
                {data.others.map((o) => (
                  <li key={o.slug}>
                    <Link
                      to={`/storie/shades/${o.slug}`}
                      data-testid={`shade-other-${o.slug}`}
                      className="flex items-center gap-4 py-4"
                    >
                      <span className="h-6 w-6 shrink-0" style={{ backgroundColor: o.hex }} />
                      <span className="font-display text-xl">{o.name}</span>
                      <ArrowRight
                        size={15}
                        className="ml-auto text-marsala transition-transform duration-500 group-hover:translate-x-1"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-10 border-t-2 border-inchiostro pt-7">
              <div className="flex flex-wrap gap-4">
                <Link to={`/componi?shade=${slug}`} data-testid="shade-cta-build" className="btn btn-solid">
                  {t('build.fromShade')}
                </Link>
                <Link to="/dove-comprare" data-testid="shade-cta-where" className="btn btn-line">
                  <MapPin size={13} strokeWidth={2.2} />
                  {t('common.findRetailer')}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};
